import { createContext, useContext, type ReactNode } from 'react';
import type { StaffingDeal } from '../../types';
import { deepLinks } from '../../utils/deepLinks';

/**
 * DASH-3 3-1 — the one place that decides where a Staffing deal name links.
 *
 * It used to choose between two eras: a frozen Affinity record (a link to something that stopped being
 * maintained at the cut, which was half the freeze disclosure) and the native IOT record panel.
 * Post-Affinity S4 buried the frozen era, so there is one destination — the record panel, keyed on
 * `crm_list_entry.id`, through the same `deepLinks.crmRecord` builder the v2 board's deal names use.
 *
 * `entry_id` and `listId` stay nullable on purpose: a row that has neither renders as plain text rather
 * than as a link to the wrong place.
 */
interface StaffingLinkContext {
  /** The IOT's `crm_list.id` — the record panel's parent list. */
  listId: number | null;
}

const StaffingSourceContext = createContext<StaffingLinkContext>({ listId: null });

export function StaffingSourceProvider({
  listId = null,
  children,
}: {
  listId?: number | null;
  children: ReactNode;
}) {
  return (
    <StaffingSourceContext.Provider value={{ listId }}>{children}</StaffingSourceContext.Provider>
  );
}

export function StaffingDealLink({
  deal,
  label,
  className,
}: {
  deal: Pick<StaffingDeal, 'name' | 'entry_id'>;
  /** What renders, when it differs from the full name (the matrix headers abbreviate). */
  label?: string;
  className?: string;
}) {
  const { listId } = useContext(StaffingSourceContext);
  const text = label ?? deal.name;

  const href =
    deal.entry_id != null && listId != null ? deepLinks.crmRecord(listId, deal.entry_id) : null;

  if (href === null) {
    return <span className={className} title={deal.name}>{text}</span>;
  }

  return (
    <a href={href} className={className} title={deal.name}>
      {text}
    </a>
  );
}
